import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { updateBusService } from '../../../domain/actions'
import { createAncillariesClickedEvent } from '../../../domain/analytics'
import { ServiceType } from '../../../domain/types'
import styles from '../GenericService/GenericService.module.css'
import serviceStyles from '../ServiceComponents.module.css'

export function BusService() {
  const intl = useIntl()
  const { service } = useService(ServiceType.Bus)

  useSendAncillaryAvailable(service, 'selectBus')

  if (!service)
    return null
  const { outboundOptions } = service

  if (!outboundOptions || values(outboundOptions).length === 0)
    return null
  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_description' })}
      glyph={getIconForService(ServiceType.Bus)}
    >
      <BusServicePerParticipant />
    </CheckoutSection>
  )
}

function BusServicePerParticipant() {
  const { service } = useService(ServiceType.Bus)

  if (!service)
    return null

  const { rooms, participants } = service

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap12)}>
      {rooms.map(({ id: roomId, name: roomName, participantIds }) => (
        <div className={cn(styles.flexContent, styles.flexContentGap16)} key={roomId}>
          {rooms.length > 1 && roomName
            ? (
                <Typography as="p" className={serviceStyles.roomName}>
                  {roomName}
                </Typography>
              )
            : null}
          {participantIds.map((participantId) => {
            const participant = participants?.[participantId]
            if (!participant || participant.availableOptions.length === 0) {
              return null
            }

            return <ParticipantRow {...participant} key={participantId} />
          })}
        </div>
      ))}
      {isEmpty(rooms)
      && Object.values(participants ?? {}).map(participant => <ParticipantRow {...participant} key={participant.id} />)}
    </div>
  )
}

function ParticipantRow({
  id,
  availableOptions,
  selectedValueOutbound,
  selectedValueNoneOption,
  name,
}: {
  id: string
  availableOptions: string[]
  name: string
  selectedValueOutbound: string | null
  selectedValueNoneOption: string | null
}) {
  const intl = useIntl()
  const { service, setServiceValue } = useService(ServiceType.Bus)
  const { sendEvent } = useAnalytics()
  const { participants, outboundOptions, noneOptions } = service || {}

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updateBusService({ participantId: id, value, direction: 'outbound' })(service))
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectBusService',
          element: 'radiobutton',
          type: outboundOptions ? outboundOptions[value ?? '']?.label ?? '' : '',
          result: outboundOptions ? outboundOptions[value ?? '']?.price ?? 0 : 0,
        }),
      )
    },
    [sendEvent, setServiceValue, service, outboundOptions, id],
  )

  if (!service)
    return null
  if (!participants)
    return null

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap8)}>
      {values(participants).length > 1 && <Typography className={serviceStyles.participantName}>{name}</Typography>}
      <div className={cn(styles.flexContent, styles.flexContentGap16)}>
        <SelectableRadioButtonGroup
          value={selectedValueOutbound ?? selectedValueNoneOption ?? undefined}
          onValueChange={handleValueChange}
        >
          <div className={cn(styles.flexContent, styles.flexContentGap8)}>
            {outboundOptions
            && values(outboundOptions).length > 0
            && (() => {
              const { id: optionId, badge, price }
                  = selectedValueOutbound && noneOptions && !noneOptions[selectedValueOutbound]
                    ? outboundOptions[selectedValueOutbound ?? '']
                    : values(outboundOptions)[0]
              return (
                <SelectableListItem value={optionId} className={styles.itemOptionWrapper} key={optionId}>
                  <div className={styles.itemOption}>
                    <div className={styles.itemOptionControl}>
                      <RadioButton value={optionId} />
                    </div>
                    <div className={styles.itemOptionControl}>
                      {badge && (
                        <Tag variant="success-tinted" size="M" className={cn(styles.itemOptionTag)}>
                          {badge}
                        </Tag>
                      )}
                      <span className={styles.optionValue}>
                        {intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_yes_label' })}
                      </span>
                    </div>
                    <div className={styles.itemOptionTrailing}>
                      <span className={styles.optionPrice}>
                        {intl.formatCurrency(price, { showSign: price < 0 })}
                      </span>
                    </div>
                  </div>
                </SelectableListItem>
              )
            })()}

            {availableOptions.map((optionId) => {
              if (noneOptions && noneOptions[optionId]) {
                const { id: noneOptionId, badge, price } = noneOptions[optionId]
                return (
                  <SelectableListItem
                    value={noneOptionId}
                    className={styles.itemOptionWrapper}
                    key={noneOptionId}
                  >
                    <div className={styles.itemOption}>
                      <div className={styles.itemOptionControl}>
                        <RadioButton value={noneOptionId} />
                      </div>
                      <div className={styles.itemOptionControl}>
                        {badge && (
                          <Tag variant="success-tinted" size="M" className={cn(styles.itemOptionTag)}>
                            {badge}
                          </Tag>
                        )}
                        <span className={styles.optionValue}>
                          {intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_no_label' })}
                        </span>
                      </div>
                      <div className={styles.itemOptionTrailing}>
                        <span className={styles.optionPrice}>{intl.formatCurrency(price, { showSign: true })}</span>
                      </div>
                    </div>
                  </SelectableListItem>
                )
              }
              return null
            })}
          </div>
        </SelectableRadioButtonGroup>
        {selectedValueOutbound && noneOptions && !noneOptions[selectedValueOutbound] && (
          <BusPickUpPoints participantId={id} />
        )}
      </div>
    </div>
  )
}

function BusPickUpPoints({
  participantId,
}: {
  participantId: string
}) {
  const intl = useIntl()
  const { service } = useService(ServiceType.Bus)

  const onClickHandler = (url: string) => {
    window.open(url, '_blank')
  }

  const showMoreThanOnePickUpPoint = useFeatureFlag('SHOW_GROUPS_BUS_MORE_THAN_ONE_PICKUP_POINT')
  const pickUpPointKnown = useFeatureFlag('SHOW_GROUPS_BUS_PICKUP_POINT_KNOWN')

  if (!service)
    return null
  const { participants, outboundOptions } = service

  const moreThanOnePickUpPoint = showMoreThanOnePickUpPoint && outboundOptions && values(outboundOptions).length > 1
  const pickUpPoint = participants[participantId].selectedValueOutbound
  const pickUpPointOption = pickUpPoint ? outboundOptions[pickUpPoint] : null

  return (
    <>
      {moreThanOnePickUpPoint && <MoreThanOnePickUpPoint participantId={participantId} /> }
      {!moreThanOnePickUpPoint && (
        <>
          <Typography variant="display-i-regular">{intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_pick_up_and_return_point' })}</Typography>
          <Banner color="neutral" size="M" hasIcon icon="MapPin">
            {pickUpPointKnown
              ? (
                  <Banner.ContentWrapper actionsPosition="bottom">
                    <Banner.TextsWrapper>
                      <Banner.Text>{pickUpPointOption?.label}</Banner.Text>
                    </Banner.TextsWrapper>
                    <Banner.ActionsWrapper actionsPosition="bottom">
                      <Button variant="secondary" size="S" onClick={() => onClickHandler(`https://www.google.com/maps/search/?q=${pickUpPointOption?.label}`)}>
                        {intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_show_on_map_text' })}
                      </Button>
                    </Banner.ActionsWrapper>
                  </Banner.ContentWrapper>
                )
              : (
                  <Banner.TextsWrapper>
                    <Banner.Text>{intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_pick_up_not_know_title' })}</Banner.Text>
                    <Banner.SupportingText>{intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_pick_up_not_know_description' })}</Banner.SupportingText>
                  </Banner.TextsWrapper>
                )}
          </Banner>
        </>
      )}
    </>
  )
}

function MoreThanOnePickUpPoint({
  participantId,
}: {
  participantId: string
}) {
  const { service, setServiceValue } = useService(ServiceType.Bus)
  const { isDesktopSm } = useBreakpoints()
  const { sendEvent } = useAnalytics()
  const intl = useIntl()

  const { participants, outboundOptions } = service ?? {}

  const handleOutboundValueChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updateBusService({ participantId, value, direction: 'outbound' })(service))
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectBusService',
          element: 'radiobutton',
          type: outboundOptions ? outboundOptions[value ?? '']?.label ?? '' : '',
          result: outboundOptions ? outboundOptions[value ?? '']?.price ?? 0 : 0,
        }),
      )
    },
    [sendEvent, setServiceValue, service, outboundOptions, participantId],
  )

  const onClickHandler = (url: string) => {
    window.open(url, '_blank')
  }

  if (!service)
    return null
  if (!participants)
    return null
  const participant = participants[participantId]

  const { availableOptions, selectedValueOutbound } = participant

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap8)}>
      {outboundOptions && values(outboundOptions).length > 0 && (
        <>
          <Typography variant="display-i-regular">
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_pick_up_and_return_point' })}
          </Typography>
          <SelectableRadioButtonGroup value={selectedValueOutbound ?? undefined} onValueChange={handleOutboundValueChange}>
            <div className={cn(styles.flexContent, styles.flexContentGap8)}>
              {outboundOptions
              && values(outboundOptions)
                .filter(({ id }) => availableOptions.includes(id))
                .map(({ id, label }) => (
                  <SelectableListItem
                    value={id}
                    className={styles.itemOptionWrapper}
                    key={id}
                  >
                    <div className={styles.itemOption}>
                      <div className={styles.itemOptionControl}>
                        <RadioButton value={id} />
                      </div>
                      <div className={styles.itemOptionControl}>
                        <span className={styles.optionValue}>{label}</span>
                      </div>
                      <div className={styles.itemOptionTrailing}>
                        <Button
                          variant="secondary"
                          size="S"
                          onClick={() => onClickHandler(`https://www.google.com/maps/search/?q=${label}`)}
                        >
                          {isDesktopSm
                            ? (
                                <>
                                  <Icon glyph="Map" size={24} />
                                  <Typography variant="body-s-bold-flat">
                                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_show_on_map_text' })}
                                  </Typography>
                                </>
                              )
                            : (
                                <Icon glyph="MapPin" size={24} />
                              )}
                        </Button>
                      </div>
                    </div>
                  </SelectableListItem>
                ))}
            </div>
          </SelectableRadioButtonGroup>
        </>
      )}
    </div>
  )
}
